/** 消息 Tab — 会话列表 + 聊天面板; 1:1 + 群聊 + 会议房间聊天合并显示 */
import {
  ArrowLeftOutlined,
  SendOutlined,
  TeamOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Empty,
  Input,
  List,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import type { InputRef } from 'antd';
import { useEffect, useRef, useState } from 'react';
import {
  type ChatMessage,
  type Conversation,
  type DirectoryUser,
  sipPhoneApi,
} from '../../api/sip-phone-api.js';

interface Props {
  currentUserId: string | undefined;
  forcedOpenConversationId?: string | null;
  refreshKey: number;
}

function fmtTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function typeIcon(t: Conversation['type']) {
  if (t === 'group') return <TeamOutlined />;
  if (t === 'conference') return <VideoCameraOutlined />;
  return <UserOutlined />;
}

function appendUniqueMessage(prev: ChatMessage[], next: ChatMessage) {
  if (prev.some((item) => item.id === next.id)) return prev;
  return [...prev, next];
}

function conversationTitle(c: Conversation, currentUserId: string | undefined) {
  if (c.type === 'p2p') {
    const peer = c.members?.find((m) => m.id !== currentUserId);
    return [peer?.name, peer?.extension].filter(Boolean).join(' ') || c.name || '1:1 会话';
  }
  return c.name ?? (c.type === 'conference' ? '会议' : '群聊');
}

export function MessagesTab({ currentUserId, forcedOpenConversationId, refreshKey }: Props) {
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMemberIds, setGroupMemberIds] = useState<string[]>([]);
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const sendingRef = useRef(false);

  const reloadConvs = () => {
    setLoadingConvs(true);
    sipPhoneApi
      .listConversations()
      .then(setConvs)
      .catch(() => message.error('加载会话失败'))
      .finally(() => setLoadingConvs(false));
  };
  useEffect(reloadConvs, [refreshKey]);

  useEffect(() => {
    if (forcedOpenConversationId) setActiveConvId(forcedOpenConversationId);
  }, [forcedOpenConversationId]);

  useEffect(() => {
    if (!activeConvId) {
      setMessages([]);
      return;
    }
    setLoadingMessages(true);
    sipPhoneApi
      .listMessages(activeConvId)
      .then((data) => {
        setMessages(data);
        setTimeout(() => scrollRef.current?.scrollTo({ top: 999999 }), 30);
      })
      .catch(() => message.error('加载消息失败'))
      .finally(() => setLoadingMessages(false));
  }, [activeConvId, refreshKey]);

  // 监听全局 sip:message 事件 — 当前会话直接 append, 其他会话 bump 会话列表
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<ChatMessage>).detail;
      if (detail.conversationId === activeConvId) {
        setMessages((prev) => appendUniqueMessage(prev, detail));
        setTimeout(() => scrollRef.current?.scrollTo({ top: 999999 }), 30);
      }
      reloadConvs();
    };
    window.addEventListener('sip:message', handler as EventListener);
    return () => window.removeEventListener('sip:message', handler as EventListener);
  }, [activeConvId]);

  useEffect(() => {
    if (creatingGroup && users.length === 0) {
      sipPhoneApi
        .listEnterprise()
        .then(setUsers)
        .catch(() => {});
    }
  }, [creatingGroup, users.length]);

  const send = async () => {
    if (!activeConvId || !draft.trim() || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    const body = draft.trim();
    setDraft('');
    try {
      const sent = await sipPhoneApi.sendMessage(activeConvId, body);
      setMessages((prev) => appendUniqueMessage(prev, sent));
      setTimeout(() => scrollRef.current?.scrollTo({ top: 999999 }), 30);
      reloadConvs();
    } catch {
      message.error('发送失败');
      setDraft(body);
    } finally {
      sendingRef.current = false;
      setSending(false);
      setTimeout(() => inputRef.current?.focus({ cursor: 'end' }), 0);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || groupMemberIds.length === 0) {
      return message.warning('请填写群名 + 至少 1 个成员');
    }
    try {
      const conv = await sipPhoneApi.createGroup(groupName.trim(), groupMemberIds);
      message.success('群聊已创建');
      setCreatingGroup(false);
      setGroupName('');
      setGroupMemberIds([]);
      reloadConvs();
      setActiveConvId(conv.id);
    } catch {
      message.error('创建群聊失败');
    }
  };

  const activeConv = convs.find((c) => c.id === activeConvId) ?? null;

  if (activeConv) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 460,
          margin: -8,
          background: '#ededed',
        }}
      >
        <Space
          style={{
            height: 48,
            padding: '0 12px',
            borderBottom: '1px solid #dedede',
            background: '#f7f7f7',
            flexShrink: 0,
          }}
        >
          <Button
            size="small"
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => setActiveConvId(null)}
          >
            返回
          </Button>
          <Typography.Text strong>{conversationTitle(activeConv, currentUserId)}</Typography.Text>
          <Tag style={{ marginInlineEnd: 0 }}>
            {typeIcon(activeConv.type)}{' '}
            {activeConv.type === 'p2p' ? '1:1' : activeConv.type === 'group' ? '群聊' : '会议'}
          </Tag>
          {activeConv.roomCode && <Tag color="purple">房间码 {activeConv.roomCode}</Tag>}
        </Space>
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '14px 12px 10px',
            background: '#ededed',
          }}
        >
          <Spin spinning={loadingMessages}>
            {messages.length === 0 ? (
              <Empty description="开始第一条消息" />
            ) : (
              messages.map((m) => {
                const mine = m.senderId === currentUserId;
                const sys = m.msgType !== 'text';
                if (sys) {
                  return (
                    <div key={m.id} style={{ textAlign: 'center', margin: '10px 0' }}>
                      <Typography.Text
                        type="secondary"
                        style={{
                          display: 'inline-block',
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 10,
                          background: 'rgba(0,0,0,0.06)',
                        }}
                      >
                        {m.body} · {fmtTime(m.sentAt)}
                      </Typography.Text>
                    </div>
                  );
                }
                return (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      justifyContent: mine ? 'flex-end' : 'flex-start',
                      marginBottom: 12,
                    }}
                  >
                    {!mine && (
                      <Avatar
                        size={28}
                        icon={<UserOutlined />}
                        style={{ marginRight: 8, flexShrink: 0, background: '#d9d9d9' }}
                      />
                    )}
                    <div
                      style={{
                        maxWidth: '76%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: mine ? 'flex-end' : 'flex-start',
                      }}
                    >
                      {!mine && (
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 10, lineHeight: '14px', marginBottom: 2 }}
                        >
                          {m.senderName}
                        </Typography.Text>
                      )}
                      <div
                        style={{
                          padding: '8px 10px',
                          borderRadius: mine ? '10px 2px 10px 10px' : '2px 10px 10px 10px',
                          background: mine ? '#1677ff' : '#fff',
                          color: mine ? '#fff' : '#1f2937',
                          boxShadow: '0 1px 1px rgba(0,0,0,0.04)',
                          wordBreak: 'break-word',
                          lineHeight: 1.55,
                        }}
                      >
                        {m.body}
                      </div>
                      <Typography.Text
                        type="secondary"
                        style={{
                          fontSize: 10,
                          display: 'block',
                          lineHeight: '14px',
                          marginTop: 2,
                        }}
                      >
                        {fmtTime(m.sentAt)}
                      </Typography.Text>
                    </div>
                  </div>
                );
              })
            )}
          </Spin>
        </div>
        <Space.Compact
          style={{
            padding: 8,
            borderTop: '1px solid #dedede',
            background: '#f7f7f7',
            flexShrink: 0,
          }}
        >
          <Input
            ref={inputRef}
            value={draft}
            onChange={(e) => {
              if (sendingRef.current) return;
              setDraft(e.target.value);
            }}
            onPressEnter={send}
            placeholder="输入消息..."
            disabled={sending}
            style={{ borderRadius: 6, background: '#fff' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={send}
            disabled={!draft.trim() || sending}
            loading={sending}
            style={{ borderRadius: 6 }}
          >
            发送
          </Button>
        </Space.Compact>
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Text strong>会话</Typography.Text>
        <Button size="small" icon={<UsergroupAddOutlined />} onClick={() => setCreatingGroup(true)}>
          新建群聊
        </Button>
      </Space>

      <Spin spinning={loadingConvs}>
        {convs.length === 0 ? (
          <Empty description="暂无会话, 去通信录开始聊天" />
        ) : (
          <List
            size="small"
            dataSource={convs}
            style={{ maxHeight: 420, overflowY: 'auto' }}
            renderItem={(c) => (
              <List.Item
                style={{ padding: '8px 4px', cursor: 'pointer' }}
                onClick={() => setActiveConvId(c.id)}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={typeIcon(c.type)}
                      style={{
                        background:
                          c.type === 'group'
                            ? '#52c41a'
                            : c.type === 'conference'
                              ? '#722ed1'
                              : '#1677ff',
                      }}
                    />
                  }
                  title={
                    <Space>
                      <Typography.Text style={{ fontSize: 13 }}>
                        {conversationTitle(c, currentUserId)}
                      </Typography.Text>
                      {c.roomCode && (
                        <Tag color="purple" style={{ fontSize: 10 }}>
                          {c.roomCode}
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {c.lastMessage?.body ? c.lastMessage.body.slice(0, 40) : '—'}
                    </Typography.Text>
                  }
                />
                <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                  {fmtTime(c.modifiedOn)}
                </Typography.Text>
              </List.Item>
            )}
          />
        )}
      </Spin>

      <Modal
        title="新建群聊"
        open={creatingGroup}
        onCancel={() => setCreatingGroup(false)}
        onOk={handleCreateGroup}
        okText="创建"
        cancelText="取消"
        destroyOnClose
      >
        <Input
          placeholder="群名称"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <Select
          mode="multiple"
          placeholder="选择成员 (至少 1 人)"
          style={{ width: '100%' }}
          value={groupMemberIds}
          onChange={setGroupMemberIds}
          showSearch
          optionFilterProp="label"
          options={users.map((u) => ({
            label: `${u.name} (${u.domainName})`,
            value: u.id,
          }))}
        />
      </Modal>
    </div>
  );
}
